import { compress, decompress } from "@/utils/compression";
import { getValueFromStorage, removeFromStorage, saveToStorage } from "@/utils/secureStore";
import type { DrawerNavigationState, ParamListBase } from "@react-navigation/native";
import { useNavigation } from "expo-router";
import { useCallback } from "react";
import { version } from "../../package.json";

export function useStoredNavigationState() {
  const navigation = useNavigation();

  const canLoadStored = async (latestRoute: string) => {
    try {
      const stateStr = await decompress((await getValueFromStorage("navigationState")) ?? "");
      if (stateStr.length > 0) {
        const savedState = JSON.parse(stateStr) as DrawerNavigationState<ParamListBase>;
        return latestRoute === savedState.routeNames[savedState.index];
      }
    } catch (error) {
      console.error(error);
    }
    return false;
  };

  const cleanHistory = useCallback(
    async (state: DrawerNavigationState<ParamListBase>) => {
      navigation.reset({
        ...(state as any),
        history: [state.history[state.history.length - 1]] as any[],
      });
    },
    [navigation],
  );

  const cleanStored = useCallback(async () => {
    try {
      await removeFromStorage("navigationState");
      await removeFromStorage("navigationStateVersion");
    } catch (error) {
      console.error("Error clearing nvigation state from storage:", error);
    }
  }, []);

  const loadStored = useCallback(async () => {
    try {
      const stateStr = await decompress((await getValueFromStorage("navigationState")) ?? "");
      if (stateStr.length > 0) {
        const savedState = JSON.parse(stateStr) as DrawerNavigationState<ParamListBase>;
        navigation.reset(savedState as any);
      } else {
        console.warn("No stored navigation to load. There is no problem.");
      }
    } catch (error) {
      console.error("Error loading navigation state from storage:", error);
    }
  }, [navigation]);

  const saveStored = useCallback(async (state: DrawerNavigationState<ParamListBase>) => {
    try {
      // Filter invalid states
      if (state.stale === false) {
        await saveToStorage("navigationState", await compress(JSON.stringify(state)));
        await saveToStorage("navigationStateVersion", `${version}.${process.env.EXPO_PUBLIC_BUILD_NUMBER}`);
      }
    } catch (error) {
      console.error("Error saving navigation state to storage:", error);
    }
  }, []);

  const validateStored = useCallback(async () => {
    try {
      const stateVersion = await getValueFromStorage("navigationStateVersion");
      if (stateVersion !== `${version}.${process.env.EXPO_PUBLIC_BUILD_NUMBER}`) {
        await cleanStored();
      }
    } catch (error) {
      console.error("Error validating navigation state from storage:", error);
    }
  }, [cleanStored]);

  return { canLoadStored, cleanHistory, cleanStored, loadStored, saveStored, validateStored };
}
